/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, eqeqeq, no-lone-blocks */
import logger from '../../../utils/logger';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import api, {
  addFavProcess,
  removeFavProcess,
  saveProcessInfo,
  editorialPublishAPI,
  contentChangeRequest,
  getNextPageGroupId,
  duplicateNode,
} from '../../../api/api';
import CustomAlert from '../../../components/CustomAlert';
import { buildProcessPath } from '../../../routes/buildProcessPath';
import { useDispatch } from 'react-redux';
import { invalidateCacheForLevel, invalidateCacheForProcess } from '../../../redux/mapDataSlice';
import { getLevelKey } from '../../../utils/getLevel';

export const useMapLevelHandlers = ({
  state,
  nodes,
  setNodes,
  edges,
  setEdges,
  currentLevel,
  currentParentId,
  processId,
  LoginUser,
  fetchNodes,
  handleLabelChange,
  goToProcess,
  addBreadcrumb,
  langMap,
  t,
}) => {
  const dispatch = useDispatch();
  const invalidateCache = (targetProcessId = null, targetLevelParam = null) => {
    if (targetProcessId && targetLevelParam) {
      // Target specific level in specific process
      const levelPattern = `${targetProcessId}_${targetLevelParam}`;
      dispatch(invalidateCacheForLevel({ levelPattern }));
    } else if (targetProcessId) {
      // Target entire process
      dispatch(invalidateCacheForProcess({ processId: targetProcessId }));
    } else {
      // Default: Invalidate current level in current process
      const levelParam = getLevelKey(currentLevel, currentParentId);
      const levelPattern = `${processId}_${levelParam}`;
      dispatch(invalidateCacheForLevel({ levelPattern }));
    }
  };
  const {
    setSelectedNodeId,
    setSelectedNode,
    setHasUnsavedChanges,
    setShowPopup,
    setPopupTitle,
    setPopupPosition,
    setIsFavorite,
    setcheckRecord,
    setShowVersionPopup,
    setShowPublishPopup,
    setShowEditorialPopup,
    setShowContentPopup,
    setrevisionData,
    setTranslationDefaults,
    setShowTranslationPopup,
  } = state;

  const addNode = useCallback(
    async (type, position, label = '') => {
      const flowContainer = document.querySelector('.flow-container');
      const containerRect = flowContainer?.getBoundingClientRect();

      let finalX = position.x;
      let finalY = position.y;

      if (containerRect) {
        finalX = position.x - containerRect.left;
        finalY = position.y - containerRect.top;

        const nodeWidth = type === 'StickyNote' ? 240 : 326;
        const nodeHeight = type === 'StickyNote' ? 180 : 90;

        if (finalX < 0) finalX = 0;
        if (finalY < 0) finalY = 0;

        if (finalX + nodeWidth > containerRect.width) finalX = containerRect.width - nodeWidth - 10;

        if (finalY + nodeHeight > containerRect.height)
          finalY = containerRect.height - nodeHeight - 10;
      }

      const GRID_SIZE = 20;
      const snap = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;
      finalX = snap(finalX);
      finalY = snap(finalY);

      const newNodeId = uuidv4().replace(/-/g, '').substring(0, 6);
      const Page_Title = 'ProcessMap';
      let PageGroupId;

      if (!nodes[0]?.page_group_id) {
        const response = await getNextPageGroupId(Page_Title);
        PageGroupId = response.next_PageGroupId;
      } else {
        PageGroupId = nodes[0]?.page_group_id;
      }

      const newNode = {
        id:
          currentParentId !== null
            ? `level${currentLevel}_${newNodeId}_${currentParentId}`
            : `level${currentLevel}_${newNodeId}`,
        data: {
          label: type === 'StickyNote' ? label : '',
          shape: type,
          onLabelChange: (newLabel) =>
            handleLabelChange(
              currentParentId !== null
                ? `level${currentLevel}_${newNodeId}_${currentParentId}`
                : `level${currentLevel}_${newNodeId}`,
              newLabel
            ),
          defaultwidt: 326,
          defaultheight: 90,
          width_height:
            type === 'StickyNote' ? { width: 240, height: 180 } : { width: 320, height: 98 },
          nodeResize: true,
          autoFocus: true,
          isClickable: true,
          updateWidthHeight: (id, size) => {
            setNodes((prevNodes) =>
              prevNodes.map((node) =>
                node.id === id ? { ...node, data: { ...node.data, width_height: size } } : node
              )
            );
          },
        },
        type: type,
        status: 'draft',
        position: { x: finalX, y: finalY },
        draggable: true,
        isNew: true,
        animated: true,
        page_title: 'ProcessMap',
        page_group_id: PageGroupId,
        selected: true,
      };

      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(newNode));
      setHasUnsavedChanges(true);
      setSelectedNodeId(newNode.id);
      setTimeout(() => {
        setNodes((nds) =>
          nds.map((node) => (node.id === newNode.id ? { ...node, isNew: false } : node))
        );
      }, 1000);
    },
    [
      nodes,
      currentParentId,
      currentLevel,
      handleLabelChange,
      setNodes,
      setHasUnsavedChanges,
      setSelectedNodeId,
    ]
  );

  const deleteNode = useCallback(() => {
    if (state.selectedNode) {
      const selectedNodeData = nodes.find((node) => node.id === state.selectedNode);

      if (selectedNodeData?.data?.LinkToStatus === true) {
        CustomAlert.warning(
          t('cannot_delete'),
          t('this_node_is_already_linked_you_cannot_delete_a_linked_node')
        );
        return;
      }
      if (state.checkRecord?.status === true) {
        CustomAlert.warning(
          t('cannot_delete'),
          t('this_node_has_objects_inside_please_delete_them_first')
        );
        return;
      }
      CustomAlert.confirm(t('delete_node'), t('are_you_sure_you_want_to_delete_this_node'), () => {
        setNodes((nds) => nds.filter((node) => node.id !== state.selectedNode));
        setEdges((eds) =>
          eds.filter(
            (edge) => edge.source !== state.selectedNode && edge.target !== state.selectedNode
          )
        );
        setSelectedNode(null);
        setShowPopup(false);
        setHasUnsavedChanges(true);
      });
    }
  }, [
    state.selectedNode,
    state.checkRecord,
    nodes,
    setNodes,
    setEdges,
    setSelectedNode,
    setShowPopup,
    setHasUnsavedChanges,
  ]);

  const handleSaveNodes = useCallback(
    async (savetype) => {
      const payload = {
        savetype,
        ...state.versionPopupPayload,
      };

      const Level = `level${currentLevel}${currentParentId ? `_${currentParentId}` : ''}`;
      const user_id = state.user && state.user.id;
      const Process_id = processId;
      const datasavetype = savetype;
      const LoginUserId = LoginUser ? LoginUser.id : null;

      try {
        if (state.versionPopupPayload) {
          await saveProcessInfo(payload);
        }

        await api.saveNodes({
          Level,
          user_id,
          Process_id,
          LoginUserId,
          datasavetype,
          nodes: nodes.map(
            ({
              id,
              data,
              type,
              position,
              draggable,
              animated,
              measured,
              page_title,
              status,
              page_group_id,
            }) => ({
              id,
              data,
              type,
              position,
              draggable,
              animated,
              measured,
              page_title,
              status,
              page_group_id,
            })
          ),
          edges: edges.map(
            ({
              id,
              source,
              target,
              markerEnd,
              animated,
              sourceHandle,
              targetHandle,
              page_title,
            }) => ({
              id,
              source,
              sourceHandle,
              target,
              targetHandle,
              markerEnd,
              animated,
              page_title,
            })
          ),
        });

        const msgT = t('nodes_saved_successfully');
        const event = new CustomEvent('modelSaved', { detail: { message: msgT || 'Model saved' } });
        window.dispatchEvent(event);
        setHasUnsavedChanges(false);
        invalidateCache();
      } catch (error) {
        logger.error('Error saving nodes:', error);
      }
    },
    [
      currentLevel,
      state.ParentPageGroupId,
      state.versionPopupPayload,
      state.user,
      processId,
      LoginUser,
      nodes,
      edges,
      setHasUnsavedChanges,
      dispatch,
    ]
  );

  const handleBack = useCallback(async () => {
    if (!state.hasUnsavedChanges) return true;

    return new Promise((resolve) => {
      CustomAlert.confirmExit(
        async () => {
          await handleSaveNodes('draft');
          resolve(true);
        },
        () => resolve(true),
        () => resolve(false),
        {
          title: t('you_have_unsaved_changes'),
          text: t('do_you_want_to_save_before_exiting'),
          confirmButtonText: t('exit_save_exit'),
          denyButtonText: t('exit_without_saving'),
          cancelButtonText: t('Cancel'),
        }
      );
    });
  }, [state.hasUnsavedChanges, handleSaveNodes]);

  const handleNodeRightClick = useCallback(
    async (event, node) => {
      event.preventDefault();
      setShowPopup(false);

      const newLevel = currentLevel + 1;
      const levelParam = `level${newLevel}_${node.id}`;
      const Process_id = processId;

      const data = await api.checkRecord(levelParam, Process_id);
      setcheckRecord(data);
      setSelectedNode(node.id);
      setPopupTitle(node.data.label || 'Node Actions');

      const flowContainer = document.querySelector('.flow-container');
      const containerRect = flowContainer.getBoundingClientRect();

      let x = event.clientX - containerRect.left;
      let y = event.clientY - containerRect.top;

      // Approximate popup dimensions to prevent overflow
      const POPUP_ESTIMATED_HEIGHT = 200;

      // If clicking near the bottom, shift popup up
      if (y + POPUP_ESTIMATED_HEIGHT > containerRect.height) {
        y = y - POPUP_ESTIMATED_HEIGHT;
        // Ensure we don't push it off the top edge
        if (y < 0) y = 10;
      }

      setPopupPosition({
        x: x,
        y: y,
      });
      setShowPopup(true);
    },
    [
      currentLevel,
      processId,
      setcheckRecord,
      setSelectedNode,
      setPopupTitle,
      setPopupPosition,
      setShowPopup,
    ]
  );

  const handleCreateNewNode = useCallback(
    async (type) => {
      if (state.selectedNode) {
        const selectedNodeData = nodes.find((node) => node.id === state.selectedNode);
        const selectedLabel = selectedNodeData?.data?.label || '';
        const TitleTranslation = selectedNodeData.data.translations || '';
        const newLevel = currentLevel + 1;

        setShowPopup(false);
        const confirmcondition = await handleBack();

        if (confirmcondition) {
          const stateData = { id: processId, selectedLabel, TitleTranslation };
          const mode = state.checkRecord?.status === true ? 'draft' : 'edit';
          const view = type === 'Swimlane' ? 'swimlane' : 'map';

          const path = buildProcessPath({
            mode: 'draft',
            view,
            processId,
            level: newLevel,
            parentId: state.selectedNode,
          });

          addBreadcrumb(`${selectedLabel}`, path, stateData);
          goToProcess({
            mode,
            view,
            processId,
            level: newLevel,
            parentId: state.selectedNode,
          });
        }
      }
    },
    [
      state.selectedNode,
      state.checkRecord,
      nodes,
      currentLevel,
      processId,
      handleBack,
      addBreadcrumb,
      goToProcess,
      setShowPopup,
    ]
  );

  const handleLanguageSwitch = useCallback(
    async (langId) => {
      if (!state.hasUnsavedChanges) {
        fetchNodes(langId);
        return;
      }

      const shouldProceed = await new Promise((resolve) => {
        CustomAlert.confirmLanguageSwitch(
          async () => {
            // Save & switch
            await handleSaveNodes('draft');
            fetchNodes(langId);
            resolve(true);
          },
          () => {
            // Don't save, just switch
            fetchNodes(langId);
            state.setHasUnsavedChanges(false);
            resolve(true);
          },
          () => {
            // Cancel
            resolve(false);
          },
          {
            title: t('save_changes_before_switching_language'),
            confirmButtonText: t('yes_save_switch'),
            cancelButtonText: t('no_discard_changes'),
          }
        );
      });
    },
    [state.hasUnsavedChanges, fetchNodes, handleSaveNodes, state.setHasUnsavedChanges]
  );

  const handleFav = useCallback(async () => {
    // const user_id = LoginUser ? LoginUser.id : null;
    const type = state.user ? state.user.type : null;

    if (!processId || !type) return;

    const PageGroupId = nodes[0]?.page_group_id;

    try {
      if (state.isFavorite) {
        await removeFavProcess(processId, PageGroupId);
        setIsFavorite(false);
      } else {
        await addFavProcess(processId, type, PageGroupId, currentParentId);
        setIsFavorite(true);
      }
    } catch (error) {
      logger.error('Favorite toggle error:', error);
    }
  }, [LoginUser, processId, state.user, nodes, state.isFavorite, setIsFavorite, currentParentId]);

  const handleNodeDragStop = useCallback(
    (event, node) => {
      setHasUnsavedChanges(true);
      const flowContainer = document.querySelector('.flow-container');
      if (!flowContainer) return;

      // Get container dimensions
      const { width: containerWidth, height: containerHeight } =
        flowContainer.getBoundingClientRect();

      // Get node dimensions. Prefer 'measured' from React Flow, fallback to data defaults
      const nodeWidth = node.measured?.width || node.data?.width_height?.width || node.width || 326;
      const nodeHeight =
        node.measured?.height || node.data?.width_height?.height || node.height || 90;

      let newX = node.position.x;
      let newY = node.position.y;

      // Clamp values
      // Ensure x is >= 0 and <= containerWidth - nodeWidth
      if (newX < 0) newX = 0;
      if (newX + nodeWidth > containerWidth) newX = containerWidth - nodeWidth;

      // Ensure y is >= 0 and <= containerHeight - nodeHeight
      if (newY < 0) newY = 0;
      if (newY + nodeHeight > containerHeight) newY = containerHeight - nodeHeight;

      // If position changed due to clamping, update it
      if (newX !== node.position.x || newY !== node.position.y) {
        setNodes((nodes) =>
          nodes.map((n) => (n.id === node.id ? { ...n, position: { x: newX, y: newY } } : n))
        );
      }
    },
    [setHasUnsavedChanges, setNodes]
  );

  const translation = useCallback(() => {
    const node = nodes.find((n) => n.id === state.selectedNode);
    if (node) {
      const defaults = state.supportedLanguages.reduce((acc, langId) => {
        const langKey = langMap[langId] || `lang_${langId}`;
        acc[langKey] = node.data?.translations?.[langKey] || '';
        return acc;
      }, {});
      setTranslationDefaults(defaults);
      setShowTranslationPopup(true);
    }
  }, [
    nodes,
    state.selectedNode,
    state.supportedLanguages,
    langMap,
    setTranslationDefaults,
    setShowTranslationPopup,
  ]);

  const updateNodeTranslations = useCallback(
    (nodeId, translations) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n;
          const langKey = langMap[state.processDefaultlanguage_id] || 'EN';
          const newLabel = translations[langKey] || n.data.label;
          return {
            ...n,
            data: {
              ...n.data,
              translations,
              label: newLabel,
            },
          };
        })
      );
      setHasUnsavedChanges(true);
    },
    [setNodes, langMap, state.processDefaultlanguage_id, setHasUnsavedChanges]
  );

  const handleNext = useCallback(
    (data) => {
      setrevisionData(data);
      if (data.editorialChange) {
        setShowPublishPopup(false);
        setShowEditorialPopup(true);
      }
      if (data.classificationChange) {
        setShowPublishPopup(false);
        setShowContentPopup(true);
      }
    },
    [setrevisionData, setShowPublishPopup, setShowEditorialPopup, setShowContentPopup]
  );

  const editorialPublish = useCallback(
    async (data) => {
      setShowEditorialPopup(false);
      const Level = `level${currentLevel}${currentParentId ? `_${currentParentId}` : ''}`;
      const payload = {
        process_id: processId,
        level: Level,
        revision_text: state.revisionData?.translations
          ? state.revisionData.translations
          : state.revisionData?.revisionText,
        requested_by: LoginUser ? LoginUser.id : null,
        schedule_type: data.scheduleType,
        scheduled_date: data.date,
        timezone: data.timezone,
      };
      await editorialPublishAPI(payload);

      const msgT = t('editorial_change_submitted_successfully');
      const event = new CustomEvent('modelSaved', {
        detail: { message: msgT || 'Editorial change submitted' },
      });
      window.dispatchEvent(event);

      await handleBack();

      setTimeout(() => {
        goToProcess({
          mode: 'draft',
          view: 'map',
          processId,
          level: currentLevel,
          parentId: currentLevel === 0 ? undefined : currentParentId,
        });
      }, 2000);
    },
    [currentLevel, currentParentId, processId, state.revisionData, LoginUser, setShowEditorialPopup]
  );

  const handleContentSubmit = useCallback(
    async (data) => {
      if (state.revisionData?.revisionText === '') {
        CustomAlert.warning(t('warning') || 'Warning', t('revision_text_is_required'));
        return false;
      }
      setShowContentPopup(false);
      const processPath = buildProcessPath({
        mode: 'draft',
        view: 'map',
        processId,
        level: currentLevel,
        parentId: currentLevel === 0 ? undefined : currentParentId,
      });
      const processLink = `${window.location.origin}${processPath}`;
      const Level = `level${currentLevel}${currentParentId ? `_${currentParentId}` : ''}`;
      const payload = {
        process_id: processId,
        level: Level,
        revision_text: state.revisionData?.translations
          ? state.revisionData.translations
          : state.revisionData?.revisionText,
        selected_language: data.emailLanguage,
        requested_by: LoginUser ? LoginUser.id : null,
        owner_id: data?.owner?.id || null,
        cc_architect: data.ccRoles.architect,
        cc_manager: data.ccRoles.manager,
        planned_publish_date: data.date,
        personal_message: data.personalMessage,
        process_title: state.title,
        process_link: processLink,
        timezone: data.timezone,
      };
      await contentChangeRequest(payload);

      const msgT = t('content_submitted_successfully');
      const event = new CustomEvent('modelSaved', {
        detail: { message: msgT || 'Content change requested' },
      });
      window.dispatchEvent(event);

      await handleBack();

      setTimeout(() => {
        goToProcess({
          mode: 'draft',
          view: 'map',
          processId,
          level: currentLevel,
          parentId: currentLevel === 0 ? undefined : currentParentId,
        });
      }, 2000);
    },
    [
      currentLevel,
      currentParentId,
      processId,
      state.revisionData,
      LoginUser,
      setShowContentPopup,
      state.title,
      goToProcess,
      handleBack,
    ]
  );

  const handleDuplicateNode = useCallback(async () => {
    if (!state.selectedNode) return;

    CustomAlert.confirm(
      t('are_you_sure') || 'Are you sure?',
      t('are_you_sure_you_want_to_duplicate_this_node_and_all_its_sub_levels'),
      async () => {
        setShowPopup(false);

        try {
          await duplicateNode({
            node_id: state.selectedNode,
            process_id: processId,
            level: `level${currentLevel}${currentParentId ? `_${currentParentId}` : ''}`, // Ensure correct level string format
            user_id: LoginUser?.id,
          });
          CustomAlert.toast(t('node_duplicated_successfully'));

          // Invalidate cache first so that stale cached data is purged
          invalidateCache(processId);

          // Refresh nodes to show the new duplicate
          // We use the same fetch implementation as initial load with forceRefresh=true
          const savedLang =
            localStorage.getItem('selectedLanguageId') ||
            sessionStorage.getItem(`process_lang_id_${processId}`);
          await fetchNodes(savedLang ? parseInt(savedLang) : state.processDefaultlanguage_id, true);
        } catch (error) {
          logger.error('Duplicate failed', error);
          CustomAlert.error(
            t('error') || 'Error',
            t('failed_to_duplicate_node') || 'Failed to duplicate node'
          );
        }
      }
    );
  }, [
    state.selectedNode,
    processId,
    currentLevel,
    currentParentId,
    LoginUser,
    fetchNodes,
    state.processDefaultlanguage_id,
    setShowPopup,
    invalidateCache,
    t,
  ]);

  return {
    addNode,
    deleteNode,
    handleSaveNodes,
    handleBack,
    handleNodeRightClick,
    handleCreateNewNode,
    handleLanguageSwitch,
    handleFav,
    handleNodeDragStop,
    translation,
    updateNodeTranslations,
    handleNext,
    editorialPublish,
    handleContentSubmit,
    goToProcess,
    handleDuplicateNode,
    invalidateCache,
  };
};
